/**
 * AuthContext: estado global de autenticación. Carga sesión persistida (token en
 * localStorage) al montar; expone logout + perfil. Login se hace exclusivamente
 * por Google OAuth desde AuthScreen.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiError } from "../api/client";
import {
  fetchMe,
  googleExchange,
  logout as apiLogout,
  updateMe as apiUpdateMe,
  type UpdateMePayload,
  type UserMe,
} from "../api/auth";
import { getToken, tryRefresh } from "../api/client";

type AuthStatus = "loading" | "authenticated" | "guest";

interface AuthContextValue {
  status: AuthStatus;
  user: UserMe | null;
  logout: () => void;
  refreshMe: () => Promise<void>;
  updateMe: (payload: UpdateMePayload) => Promise<UserMe>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserMe | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  // Restore session on mount, también detecta callback de Google OAuth
  useEffect(() => {
    // 1) ¿Venimos del callback de Google? URL contiene ?code=...&state=...
    if (window.location.pathname === "/auth/google/callback") {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const error = params.get("error");
      const expectedState = sessionStorage.getItem("oauth_state");
      sessionStorage.removeItem("oauth_state");
      if (error) {
        // El usuario canceló o hubo error
        window.history.replaceState({}, "", "/");
        setStatus("guest");
        return;
      }
      if (code) {
        googleExchange(code, state ?? undefined, expectedState ?? undefined)
          .then(() => fetchMe())
          .then((u) => {
            setUser(u);
            setStatus("authenticated");
            window.history.replaceState({}, "", "/");
          })
          .catch((e) => {
            console.error("Google OAuth exchange failed:", e);
            window.history.replaceState({}, "", "/");
            setStatus("guest");
          });
        return;
      }
    }

    // 2) Sesión normal. Si tenemos access en localStorage, vamos directo a
    // /auth/me — si el access expiró, el cliente hace auto-refresh con la
    // cookie httpOnly y reintenta. Si NO hay access (localStorage limpio,
    // primer load), igual intentamos refresh por si la cookie sigue viva
    // del último login (el user vuelve días después y sigue logueado).
    const token = getToken();
    const bootstrap = token ? Promise.resolve(true) : tryRefresh();

    bootstrap
      .then((ok) => {
        if (!ok) {
          setStatus("guest");
          return;
        }
        return fetchMe().then((u) => {
          setUser(u);
          setStatus("authenticated");
        });
      })
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          apiLogout();
        }
        setStatus("guest");
      });
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget: el await no es necesario para la UX (limpieza local
    // sucede igual y la cookie expira por max-age aunque no llegue el POST).
    apiLogout();
    setUser(null);
    setStatus("guest");
  }, []);

  const refreshMe = useCallback(async () => {
    const u = await fetchMe();
    setUser(u);
  }, []);

  const updateMe = useCallback(async (payload: UpdateMePayload) => {
    const u = await apiUpdateMe(payload);
    setUser(u);
    return u;
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, logout, refreshMe, updateMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() debe estar dentro de <AuthProvider>");
  return ctx;
}
